import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Package, Phone, MapPin, DollarSign, CheckCircle2, X, ChevronRight } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { useVendor } from '../context/VendorContext';
import { VendorOrder, Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Button } from '../components/ui/Button';
export function VendorOrdersScreen() {
  const {
    navigate
  } = useNavigation();
  const {
    orders,
    updateOrderStatus
  } = useVendor();
  const [activeTab, setActiveTab] = useState<VendorOrder['orderStatus']>('New');
  const tabs: VendorOrder['orderStatus'][] = ['New', 'Processing', 'Shipped', 'Delivered', 'Cancelled'];
  const filteredOrders = orders.filter(order => order.orderStatus === activeTab);
  const handleAcceptOrder = (orderId: string) => {
    updateOrderStatus(orderId, 'Processing');
  };
  const handleRejectOrder = (orderId: string) => {
    updateOrderStatus(orderId, 'Cancelled');
  };
  const handleMarkAsShipped = (orderId: string) => {
    updateOrderStatus(orderId, 'Shipped');
  };
  const getStatusColor = (status: VendorOrder['orderStatus']) => {
    switch (status) {
      case 'New':
        return 'bg-warning/10 text-warning';
      case 'Processing':
        return 'bg-primary/10 text-primary';
      case 'Shipped':
        return 'bg-accent/10 text-accent';
      case 'Delivered':
        return 'bg-success/10 text-success';
      case 'Cancelled':
        return 'bg-error/10 text-error';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="min-h-screen bg-gray-50 pb-24" data-id="element-2131">
      <Header title="Orders Management" showBack={false} data-id="element-2132" />

      {/* Tabs */}
      <div className="bg-white px-4 border-b border-gray-100 sticky top-[53px] z-30 overflow-x-auto" data-id="element-2133">
        <div className="flex gap-2 min-w-max" data-id="element-2134">
          {tabs.map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-gray-500'}`} data-id="element-2135">
              {tab}
              <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-gray-100" data-id="element-2136">
                {orders.filter(o => o.orderStatus === tab).length}
              </span>
            </button>)}
        </div>
      </div>

      {/* Orders List */}
      <div className="p-4 space-y-3" data-id="element-2137">
        {filteredOrders.length > 0 ? filteredOrders.map(order => <motion.div key={order.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-2xl shadow-sm border border-gray-50 overflow-hidden" data-id="element-2138">
              {/* Order Header */}
              <div className="p-4 border-b border-gray-50 bg-gray-50/50 cursor-pointer" onClick={() => navigate(Screen.VENDOR_ORDER_DETAIL, {
          orderId: order.id
        })} data-id="element-2139">
                <div className="flex items-center justify-between mb-2" data-id="element-2140">
                  <span className="text-sm font-bold text-gray-900" data-id="element-2141">
                    {order.id}
                  </span>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${getStatusColor(order.orderStatus)}`} data-id="element-2142">
                    {order.orderStatus}
                  </span>
                </div>
                <p className="text-xs text-gray-500" data-id="element-2143">
                  {new Date(order.date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
                </p>
              </div>

              {/* Product Info */}
              <div className="p-4 flex gap-3" data-id="element-2144">
                <img src={order.product.image} alt={order.product.name} className="w-20 h-20 rounded-xl object-cover" data-id="element-2145" />
                <div className="flex-1" data-id="element-2146">
                  <h3 className="font-medium text-gray-900 mb-1" data-id="element-2147">
                    {order.product.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-2" data-id="element-2148">
                    Qty: {order.product.quantity}
                  </p>
                  <p className="text-lg font-bold text-primary" data-id="element-2149">
                    ₦{order.total.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Customer Info */}
              <div className="px-4 pb-4 space-y-2" data-id="element-2150">
                <div className="flex items-center gap-2 text-sm" data-id="element-2151">
                  <Package className="w-4 h-4 text-gray-400" data-id="element-2152" />
                  <span className="text-gray-600" data-id="element-2153">Customer:</span>
                  <span className="font-medium text-gray-900" data-id="element-2154">
                    {order.buyerName}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm" data-id="element-2155">
                  <Phone className="w-4 h-4 text-gray-400" data-id="element-2156" />
                  <span className="text-gray-600" data-id="element-2157">{order.buyerPhone}</span>
                </div>
                <div className="flex items-start gap-2 text-sm" data-id="element-2158">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5" data-id="element-2159" />
                  <span className="text-gray-600 flex-1" data-id="element-2160">
                    {order.shippingAddress}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm" data-id="element-2161">
                  <DollarSign className="w-4 h-4 text-gray-400" data-id="element-2162" />
                  <span className="text-gray-600" data-id="element-2163">Payment:</span>
                  <span className={`font-medium ${order.paymentStatus === 'Paid' ? 'text-success' : 'text-warning'}`} data-id="element-2164">
                    {order.paymentStatus}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              {order.orderStatus === 'New' && <div className="px-4 pb-4 flex gap-2" data-id="element-2165">
                  <Button variant="outline" size="sm" onClick={() => handleRejectOrder(order.id)} className="flex-1 border-error text-error hover:bg-error/5" icon={<X className="w-4 h-4" data-id="element-2167" />} data-id="element-2166">
                    Reject
                  </Button>
                  <Button size="sm" onClick={() => handleAcceptOrder(order.id)} className="flex-1" icon={<CheckCircle2 className="w-4 h-4" data-id="element-2169" />} data-id="element-2168">
                    Accept
                  </Button>
                </div>}

              {order.orderStatus === 'Processing' && <div className="px-4 pb-4" data-id="element-2170">
                  <Button fullWidth size="sm" onClick={() => handleMarkAsShipped(order.id)} icon={<Package className="w-4 h-4" data-id="element-2172" />} data-id="element-2171">
                    Mark as Shipped
                  </Button>
                </div>}
            </motion.div>) : <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-2173">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-2174">
              <Package className="w-10 h-10 text-gray-400" data-id="element-2175" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-2176">
              No {activeTab} Orders
            </h3>
            <p className="text-sm text-gray-500" data-id="element-2177">
              You don't have any {activeTab.toLowerCase()} orders at the moment.
            </p>
          </div>}
      </div>
    </motion.div>;
}